import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Vote,
  Crown,
  Lock,
  Unlock,
  CheckCircle2,
  Users,
  Clock,
  Sparkles,
  ArrowRight,
  Loader2,
  MessageSquare,
  Send,
  Reply,
  X,
  Camera,
  Image as ImageIcon,
  Upload,
  Maximize2,
  Smartphone,
  Plus,
  Trash2,
  Check,
  ChevronRight,
  Eye,
  Trophy,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import Username from "@/components/Username";

const REACTIONS = [
  { type: "like", emoji: "👍", label: "Suka", bgHover: "hover:bg-blue-500/20" },
  { type: "love", emoji: "❤️", label: "Love", bgHover: "hover:bg-rose-500/20" },
  { type: "haha", emoji: "🤣", label: "Ngakak", bgHover: "hover:bg-amber-500/20" },
  { type: "fire", emoji: "🔥", label: "Membara", bgHover: "hover:bg-orange-500/20" },
  { type: "skull", emoji: "💀", label: "Lawak", bgHover: "hover:bg-purple-500/20" },
];

// In-game SVG placeholder previews (so there's guaranteed crisp sample proof imagery)
const SAMPLE_PROOF_GAME =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675' viewBox='0 0 1200 675'><rect width='1200' height='675' fill='%230f050d'/><circle cx='950' cy='280' r='320' fill='%23be123c' opacity='0.25'/><circle cx='250' cy='450' r='250' fill='%234c0519' opacity='0.35'/><path d='M200 675 L450 250 L650 675 Z' fill='%231c0d18' opacity='0.7'/><path d='M550 675 L800 180 L1050 675 Z' fill='%23280c20' opacity='0.6'/><rect x='60' y='460' width='1080' height='175' rx='20' fill='%23090206' opacity='0.92' stroke='%23e11d48' stroke-width='3'/><rect x='90' y='425' width='220' height='40' rx='10' fill='%23be123c'/><text x='115' y='452' fill='white' font-family='sans-serif' font-size='18' font-weight='bold'>Miyuki (Heroine)</text><text x='95' y='515' fill='%23fecdd3' font-family='sans-serif' font-size='22' font-weight='bold'>Sensei, apa kamu yakin mau lanjutin sesi belajar tengah malam ini...?</text><text x='95' y='555' fill='%23fda4af' font-family='sans-serif' font-size='20'>Hujan di luar deras banget lho, jangan sampai ada yang liat kita berdua di sini...</text><rect x='900' y='40' width='250' height='45' rx='12' fill='%2318040f' stroke='%23fda4af' stroke-width='1.5'/><text x='920' y='70' fill='%23f43f5e' font-family='sans-serif' font-size='16' font-weight='bold'>kyoukaTL • Build v0.85</text></svg>";

const SAMPLE_PROOF_ANDROID =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675' viewBox='0 0 1200 675'><rect width='1200' height='675' fill='%2308070d'/><rect x='40' y='40' width='540' height='595' rx='24' fill='%2313091f' stroke='%238b5cf6' stroke-width='2.5'/><rect x='620' y='40' width='540' height='595' rx='24' fill='%231a0f0f' stroke='%23f97316' stroke-width='2.5'/><rect x='60' y='60' width='200' height='36' rx='8' fill='%237c3aed'/><text x='75' y='85' fill='white' font-family='sans-serif' font-size='16' font-weight='bold'>HD 720p (Ukuran 2.1 GB)</text><text x='60' y='140' fill='%23c4b5fd' font-family='sans-serif' font-size='20' font-weight='bold'>Visual Tajam & Audio Lossless</text><text x='60' y='180' fill='%23a78bfa' font-family='sans-serif' font-size='15'>Tekstur HD tidak dikompres, 60 FPS mulus.</text><circle cx='310' cy='380' r='140' fill='%236d28d9' opacity='0.4'/><rect x='640' y='60' width='220' height='36' rx='8' fill='%23ea580c'/><text x='655' y='85' fill='white' font-family='sans-serif' font-size='16' font-weight='bold'>Burix Mode (Ukuran 950 MB)</text><text x='640' y='140' fill='%23fed7aa' font-family='sans-serif' font-size='20' font-weight='bold'>Ramah HP Kentang & Kuota</text><text x='640' y='180' fill='%23fb923c' font-family='sans-serif' font-size='15'>Kompresi tekstur ringan, hemat RAM 2GB.</text><circle cx='890' cy='380' r='140' fill='%23c2410c' opacity='0.4'/><rect x='450' y='570' width='300' height='45' rx='12' fill='%23000000' stroke='%23facc15' stroke-width='2'/><text x='485' y='600' fill='%23fef08a' font-family='sans-serif' font-size='17' font-weight='bold'>Pilih di Voting Bawah 📲</text></svg>";

const DEFAULT_POLLS = [
  {
    id: "poll_engine",
    title: "Next Update Mau Game Engine Apa? 🕹️",
    category: "Pilihan Engine ⚙️",
    description:
      "Tentukan engine game favorit kalian untuk proyek terjemahan berikutnya yang akan digarap Founder Kyouka Reimei! Hak suara eksklusif Hardcore Cultist & God Tier.",
    min_role: "hardcore_cultist",
    ends_in: "Sisa 5 Hari",
    expires_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    total_votes: 142,
    proof_image: SAMPLE_PROOF_GAME,
    proof_title: "Bukti Komparasi Uji Coba Engine In-Game",
    proof_desc:
      "Riset ekstraksi skrip, voice synchronization, dan rendering font Indo di Unity, RPG Maker, dan Ren'Py.",
    options: [
      {
        id: "opt_eng_1",
        title: "Unity Engine",
        tag: "Grafis Modern 2D/3D • Efek Partikel Tajam • Animasi Halus",
        votes: 68,
        percentage: 47.9,
        badge: "Paling Populer 🌟",
      },
      {
        id: "opt_eng_2",
        title: "RPG Maker (RPGM)",
        tag: "Gameplay RPG Klasik • Eksplorasi Luas • Cerita Padat",
        votes: 49,
        percentage: 34.5,
        badge: "Favorit Cultist ⚔️",
      },
      {
        id: "opt_eng_3",
        title: "Ren'Py",
        tag: "Visual Novel Interaktif • Full Voice • Story Rich",
        votes: 25,
        percentage: 17.6,
        badge: "Story Rich 📖",
      },
    ],
    comments: [
      {
        id: "cm_eng_1",
        user_name: "Alvan Hardcore",
        user_role: "hardcore_cultist",
        badges: [],
        voted_for_title: "Unity Engine",
        message:
          "Vote Unity bang! Efek grafisnya modern banget dan enak dinikmati, apalagi kalau resolusi full HD!",
        created_at: "1 jam yang lalu",
        reactions: { like: 12, fire: 18, love: 14 },
        user_reaction: "fire",
        replies: [],
      },
      {
        id: "cm_eng_2",
        user_name: "Kyouka Reimei",
        user_role: "admin",
        badges: ["cult_master"],
        voted_for_title: "RPG Maker",
        message:
          "RPG Maker asik gameplay-nya berjam-jam, tapi Unity juga visualnya mantep. Gaskeun tentukan lewat voting ini!",
        created_at: "45 menit yang lalu",
        reactions: { like: 19, love: 28, skull: 4 },
        user_reaction: "love",
        replies: [],
      },
    ],
  },
  {
    id: "poll_genre",
    title: "Kalian Lebih Suka Genre Game Apa Buat Next Project? 🎭",
    category: "Pilihan Genre 🏷️",
    description:
      "Vote preferensi genre cerita favorit kalian untuk game rilisan bulan ini. Hak suara eksklusif untuk Hardcore Cultist & God Tier!",
    min_role: "hardcore_cultist",
    ends_in: "Sisa 4 Hari",
    expires_at: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
    total_votes: 118,
    proof_image: SAMPLE_PROOF_GAME,
    proof_title: "Preview Naskah Cerita & Dialog In-Game",
    proof_desc:
      "Sneak peek naskah dialog terjemahan oleh Founder Kyouka Reimei.",
    options: [
      {
        id: "opt_gen_1",
        title: "NTR (Netorare)",
        tag: "Drama Intens • Konflik Emosional Panas & Greget",
        votes: 56,
        percentage: 47.5,
        badge: "Jalur Gelap 🔥",
      },
      {
        id: "opt_gen_2",
        title: "Pure Love / No NTR",
        tag: "Romantis Wholesome • Tanpa Patah Hati & Cerita Manis",
        votes: 44,
        percentage: 37.3,
        badge: "Jalur Terang 💖",
      },
      {
        id: "opt_gen_3",
        title: "Bebas / Campuran",
        tag: "Ikuti Selera & Kurasi Founder Kyouka Reimei",
        votes: 18,
        percentage: 15.2,
        badge: "Founder Pick 👑",
      },
    ],
    comments: [
      {
        id: "cm_gen_1",
        user_name: "Shadow Cultist",
        user_role: "hardcore_cultist",
        badges: [],
        voted_for_title: "NTR",
        message:
          "NTR lebih menantang emosi wkwkwk, drama-nya dapet banget bikin penasaran sampe tamat!",
        created_at: "2 jam yang lalu",
        reactions: { haha: 16, fire: 14 },
        user_reaction: "fire",
        replies: [],
      },
      {
        id: "cm_gen_2",
        user_name: "Alvan Hardcore",
        user_role: "hardcore_cultist",
        badges: [],
        voted_for_title: "Pure Love",
        message:
          "Sekali-kali yang Pure Love wholesome bang, biar hati adem damai haha!",
        created_at: "1 jam yang lalu",
        reactions: { love: 22, like: 15 },
        user_reaction: "love",
        replies: [],
      },
    ],
  },
  {
    id: "poll_port_android_2026",
    title: "Voting Spesifikasi Porting Android: Monster Black Market DLC 2",
    category: "Porting Android 📱",
    description:
      "Game Monster Black Market DLC 2 lagi tahap finishing. Kalian lebih butuh format APK resolusi HD 720p (2GB) atau versi hemat/burix (1GB) buat HP kentang?",
    min_role: "hardcore_cultist",
    ends_in: "Sisa 3 Hari",
    expires_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    total_votes: 94,
    proof_image: SAMPLE_PROOF_ANDROID,
    proof_title: "Bukti Komparasi Uji Coba APK Android (Test Build)",
    proof_desc:
      "Perbandingan build HD 720p vs kompresi tekstur hemat memori di emulator/perangkat Android test bed.",
    options: [
      {
        id: "opt_p1",
        title: "HD 720p Kualitas Maksimal (~2.1 GB)",
        tag: "Grafis Super Bening • Tekstur Asli 60 FPS • Audio Lossless",
        votes: 49,
        percentage: 52.1,
        badge: "Paling Diminati 🌟",
      },
      {
        id: "opt_p2",
        title: "Lebih Burix / Hemat Memori (~950 MB)",
        tag: "Tekstur Kompres Maksimal • Ramah HP Kentang & Hemat Kuota",
        votes: 45,
        percentage: 47.9,
        badge: "Ramah HP Kentang 📱",
      },
    ],
    comments: [],
  },
];

const DEFAULT_POLL_IDS = new Set([
  "poll_engine",
  "poll_genre",
  "poll_port_android_2026",
  "poll_game_nov2026",
]);

function isCustomLocalPolls(rawStr) {
  if (!rawStr) return false;
  try {
    const arr = JSON.parse(rawStr);
    if (!Array.isArray(arr)) return false;
    if (arr.length !== DEFAULT_POLLS.length) return true;
    for (let i = 0; i < arr.length; i++) {
      const p = arr[i];
      const def = DEFAULT_POLLS[i];
      if (!p || !def) return true;
      if (!DEFAULT_POLL_IDS.has(p.id)) return true;
      if (
        p.title !== def.title ||
        p.category !== def.category ||
        p.description !== def.description
      ) {
        return true;
      }
      if ((p.options || []).length !== (def.options || []).length) return true;
      for (let j = 0; j < p.options.length; j++) {
        if (p.options[j].title !== def.options[j].title) return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

function formatCommentTime(ts) {
  if (!ts) return "Baru saja";
  if (typeof ts === "string" && !ts.includes("T")) return ts;
  try {
    const dt = new Date(ts);
    if (Number.isNaN(dt.getTime())) return String(ts);
    const diffSec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (diffSec < 60) return "Baru saja";
    const mins = Math.floor(diffSec / 60);
    if (mins < 60) return `${mins} menit yang lalu`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} jam yang lalu`;
    const days = Math.floor(hours / 24);
    return `${days} hari yang lalu`;
  } catch {
    return String(ts);
  }
}

function compressImageDataUrl(file, maxDim = 1280, quality = 0.84) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
      const rawUrl = ev.target.result;
      const img = new window.Image();
      img.onerror = () => resolve(rawUrl);
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width <= maxDim && height <= maxDim && file.size <= 500 * 1024) {
            resolve(rawUrl);
            return;
          }
          if (width > height && width > maxDim) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else if (height > maxDim) {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch {
          resolve(rawUrl);
        }
      };
      img.src = rawUrl;
    };
    reader.readAsDataURL(file);
  });
}

export default function VotingWidget() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const syncingRef = useRef(false);

  // Polls storage & state (cached in localStorage for instant render, authoritative in MongoDB)
  const [polls, setPolls] = useState(() => {
    try {
      const saved = localStorage.getItem("kyouka_multi_polls_v2");
      return saved ? JSON.parse(saved) : DEFAULT_POLLS;
    } catch {
      return DEFAULT_POLLS;
    }
  });

  const [activePollId, setActivePollId] = useState(() => {
    return polls[0]?.id || "poll_engine";
  });

  // Track user's votes: { [pollId]: optionId }
  const [userVotes, setUserVotes] = useState(() => {
    try {
      const saved = localStorage.getItem("kyouka_user_votes_v2");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [submittingVote, setSubmittingVote] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [syncingServer, setSyncingServer] = useState(false);

  // Image Modal View
  const [viewingImage, setViewingImage] = useState(null);

  // Founder "+ Buat Voting Baru" Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState("Proyek Terjemahan 🎮");
  const [newMinRole, setNewMinRole] = useState("hardcore_cultist");
  const [newProofDesc, setNewProofDesc] = useState("");
  const [newProofImage, setNewProofImage] = useState(null);
  const [newExpiresCustom, setNewExpiresCustom] = useState(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() + 7);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  });
  const [newOptions, setNewOptions] = useState([
    { title: "", tag: "" },
    { title: "", tag: "" },
  ]);

  // Comment state
  const [commentText, setCommentText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTargetUser, setReplyTargetUser] = useState("");
  const [replyText, setReplyText] = useState("");
  const [activeReactionPicker, setActiveReactionPicker] = useState(null);

  // Eligibility check
  const isFounder = user?.role === "admin";

  // Persist polls & votes to local cache
  const savePolls = (newPolls) => {
    setPolls(newPolls);
    try {
      localStorage.setItem("kyouka_multi_polls_v2", JSON.stringify(newPolls));
    } catch (e) {
      console.error(e);
    }
  };

  const saveVotes = (newVotes) => {
    setUserVotes(newVotes);
    try {
      localStorage.setItem("kyouka_user_votes_v2", JSON.stringify(newVotes));
    } catch (e) {
      console.error(e);
    }
  };

  // Sync with MongoDB backend & auto-migrate Founder's PC localStorage once
  const syncFromServer = async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      const { data } = await api.get("/community/polls");
      const rawLocal = localStorage.getItem("kyouka_multi_polls_v2");
      const alreadyMigrated = localStorage.getItem("kyouka_polls_migrated_v1");

      // If Founder's PC has custom polls in localStorage that haven't been pushed yet:
      if (isFounder && !alreadyMigrated && isCustomLocalPolls(rawLocal)) {
        const localPolls = JSON.parse(rawLocal);
        const res = await api.post("/community/polls/import", {
          polls: localPolls,
          force: true,
        });
        localStorage.setItem("kyouka_polls_migrated_v1", "1");
        if (Array.isArray(res.data?.polls)) {
          savePolls(res.data.polls);
          setActivePollId((prev) =>
            res.data.polls.some((p) => p.id === prev)
              ? prev
              : res.data.polls[0]?.id || null
          );
        }
        toast.success("☁️ Setelan voting dari PC kamu berhasil disinkronkan ke server!");
        return;
      }

      if (Array.isArray(data?.polls)) {
        if (data.initialized) {
          localStorage.setItem("kyouka_polls_migrated_v1", "1");
        }
        savePolls(data.polls);
        setActivePollId((prev) =>
          data.polls.some((p) => p.id === prev)
            ? prev
            : data.polls[0]?.id || null
        );
      }

      if (data?.my_votes && typeof data.my_votes === "object") {
        saveVotes({ ...userVotes, ...data.my_votes });
      }
    } catch (err) {
      // silent fallback to cached polls if offline
    } finally {
      syncingRef.current = false;
    }
  };

  useEffect(() => {
    syncFromServer();
    const interval = setInterval(() => {
      if (!showCreateModal) {
        syncFromServer();
      }
    }, 15000);
    const onFocus = () => {
      if (!showCreateModal) syncFromServer();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder, user?.id, showCreateModal]);

  // Manual 1-click force sync button for Founder
  const handleForceSyncToServer = async () => {
    if (!isFounder || syncingServer) return;
    setSyncingServer(true);
    try {
      const res = await api.post("/community/polls/import", {
        polls,
        force: true,
      });
      localStorage.setItem("kyouka_polls_migrated_v1", "1");
      if (Array.isArray(res.data?.polls)) {
        savePolls(res.data.polls);
      }
      toast.success("☁️ Semua topik voting berhasil disinkronkan ke database server! Semua device sekarang sama.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal sinkronisasi ke server.");
    } finally {
      setSyncingServer(false);
    }
  };

  // Current active poll
  const activePoll = polls.find((p) => p.id === activePollId) || polls[0];
  const activeProofImage =
    activePoll?.proof_image ||
    (activePoll?.id === "poll_port_android_2026"
      ? SAMPLE_PROOF_ANDROID
      : SAMPLE_PROOF_GAME);
  const userCurrentVote = activePoll ? userVotes[activePoll.id] : null;

  // Sync selectedOptionId when switching polls or when server votes arrive
  useEffect(() => {
    if (activePoll) {
      setSelectedOptionId(userVotes[activePoll.id] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePollId, userVotes]);

  // Expiration & countdown helper
  const formatRemainingTime = (expiresAt, fallback) => {
    if (!expiresAt) return fallback || "Aktif";
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return "Voting Telah Ditutup 🏁";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `Sisa ${days} Hari ${hours} Jam`;
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `Sisa ${hours} Jam ${mins} Menit`;
  };

  const isExpired = activePoll?.expires_at
    ? new Date(activePoll.expires_at).getTime() <= Date.now()
    : false;

  const highestVote = activePoll
    ? Math.max(...(activePoll.options || []).map((o) => o.votes || 0))
    : 0;

  // Founder Delete Poll (Synced to MongoDB)
  const handleDeletePoll = async (pollId) => {
    const p = polls.find((x) => x.id === pollId);
    if (!window.confirm(`Yakin ingin menghapus topik voting "${p?.title || "ini"}" di semua perangkat?`)) return;
    const filtered = polls.filter((x) => x.id !== pollId);
    savePolls(filtered);
    if (activePollId === pollId) {
      setActivePollId(filtered[0]?.id || null);
    }
    try {
      await api.delete(`/community/polls/${pollId}`);
      localStorage.setItem("kyouka_polls_migrated_v1", "1");
      toast.success("Topik voting berhasil dihapus dari server! 🗑️");
    } catch (err) {
      // If poll wasn't in DB yet, force-import the filtered list
      try {
        await api.post("/community/polls/import", { polls: filtered, force: true });
        localStorage.setItem("kyouka_polls_migrated_v1", "1");
        toast.success("Topik voting berhasil dihapus dari server! 🗑️");
      } catch (e2) {
        toast.error("Gagal menghapus dari server, coba lagi.");
      }
    }
  };

  const userRole = user?.role || "guest";
  const requiredRole = activePoll?.min_role || "hardcore_cultist";

  // Check if user has Hardcore+ privilege
  const hasHardcoreAccess =
    userRole === "admin" ||
    userRole === "god_tier_supporter" ||
    userRole === "hardcore_cultist";

  const canVoteAndComment =
    requiredRole === "all" ? !!user : hasHardcoreAccess;

  // "Kekunci tapi ga di blur gitu loh biar orang penasaran"
  const canAccessFullProof =
    requiredRole === "all" ? true : hasHardcoreAccess;

  // Handle Voting (Synced to MongoDB)
  const handleVote = async () => {
    if (!user) {
      toast.info("Silakan login terlebih dahulu untuk ikut voting.");
      return;
    }
    if (!canVoteAndComment) {
      toast.error(
        "Voting ini eksklusif untuk member Hardcore Cultist & God Tier Supporter!"
      );
      return;
    }
    if (!selectedOptionId || !activePoll) {
      toast.info("Pilih salah satu opsi voting terlebih dahulu.");
      return;
    }

    setSubmittingVote(true);
    try {
      const { data } = await api.post(`/community/polls/${activePoll.id}/vote`, {
        option_id: selectedOptionId,
      });
      if (data?.poll) {
        const updatedPolls = polls.map((p) =>
          p.id === activePoll.id ? data.poll : p
        );
        savePolls(updatedPolls);
      }
      saveVotes({ ...userVotes, [activePoll.id]: selectedOptionId });
      toast.success("Suara votingmu berhasil dicatat di server! 🎉");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Gagal mencatat suara voting, coba lagi."
      );
    } finally {
      setSubmittingVote(false);
    }
  };

  // Founder Upload Custom Proof Image for Current Poll (Synced to MongoDB)
  const handleProofImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePoll) return;

    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 8MB.");
      return;
    }

    try {
      const base64 = await compressImageDataUrl(file);
      const updated = polls.map((p) =>
        p.id === activePoll.id ? { ...p, proof_image: base64 } : p
      );
      savePolls(updated);
      await api.patch(`/community/polls/${activePoll.id}`, {
        proof_image: base64,
      });
      toast.success("Foto bukti translasi berhasil disimpan ke server! 📸");
    } catch (err) {
      toast.error("Gagal mengunggah foto bukti ke server.");
    }
  };

  // Founder Create New Poll (Synced to MongoDB)
  const handleAddPollOption = () => {
    if (newOptions.length >= 6) {
      toast.info("Maksimal 6 opsi voting.");
      return;
    }
    setNewOptions([...newOptions, { title: "", tag: "" }]);
  };

  const handleRemovePollOption = (idx) => {
    if (newOptions.length <= 2) {
      toast.info("Minimal harus ada 2 opsi voting.");
      return;
    }
    setNewOptions(newOptions.filter((_, i) => i !== idx));
  };

  const handleCreatePoll = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) {
      toast.info("Judul voting wajib diisi.");
      return;
    }

    const validOptions = newOptions.filter((o) => o.title.trim().length > 0);
    if (validOptions.length < 2) {
      toast.error("Minimal isi 2 judul opsi voting!");
      return;
    }

    const expiresIso = newExpiresCustom
      ? new Date(newExpiresCustom).toISOString()
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    const payload = {
      title: newTitle.trim(),
      category: newCategory,
      description: newDesc.trim() || "Polling diskusi dan penentuan komunitas.",
      min_role: newMinRole,
      expires_at: expiresIso,
      ends_in: formatRemainingTime(expiresIso, "Sisa 7 Hari"),
      proof_image: newProofImage || null,
      proof_title: "Bukti Pengerjaan & Preview Proyek",
      proof_desc: newProofDesc.trim() || "Lampiran bukti langsung dari Founder.",
      options: validOptions.map((o, idx) => ({
        id: `opt_${Date.now()}_${idx}`,
        title: o.title.trim(),
        tag: o.tag.trim() || "Pilihan Komunitas",
        votes: 0,
        badge: idx === 0 ? "Opsi Utama 🌟" : null,
      })),
    };

    try {
      const { data } = await api.post("/community/polls", payload);
      localStorage.setItem("kyouka_polls_migrated_v1", "1");
      if (data?.poll) {
        const updated = [data.poll, ...polls];
        savePolls(updated);
        setActivePollId(data.poll.id);
      }
      setShowCreateModal(false);
      setNewTitle("");
      setNewDesc("");
      setNewProofDesc("");
      setNewProofImage(null);
      setNewOptions([
        { title: "", tag: "" },
        { title: "", tag: "" },
      ]);
      toast.success("Voting baru berhasil diterbitkan di semua perangkat! 🚀");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Gagal menerbitkan topik voting ke server."
      );
    }
  };

  // Comment Handlers (Synced to MongoDB)
  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Silakan login untuk mengirim komentar.");
      return;
    }
    if (!canVoteAndComment) {
      toast.error("Ruang diskusi ini khusus untuk member Hardcore Cultist ke atas!");
      return;
    }
    if (!commentText.trim() || commentText.trim().length < 2 || !activePoll) {
      toast.info("Tulis pesan komentarmu terlebih dahulu.");
      return;
    }

    const msg = commentText.trim();
    setCommentText("");

    try {
      const { data } = await api.post(`/community/polls/${activePoll.id}/comments`, {
        message: msg,
      });
      if (data?.comment) {
        const updatedPolls = polls.map((p) =>
          p.id === activePoll.id
            ? { ...p, comments: [data.comment, ...(p.comments || [])] }
            : p
        );
        savePolls(updatedPolls);
      }
      toast.success("Komentarmu berhasil dikirim ke ruang diskusi! 💬");
    } catch (err) {
      setCommentText(msg);
      toast.error(
        formatApiError(err.response?.data?.detail) || "Gagal mengirim komentar."
      );
    }
  };

  const handleSubmitReply = async (commentId) => {
    if (!user) {
      toast.info("Silakan login untuk membalas.");
      return;
    }
    if (!canVoteAndComment) {
      toast.error("Ruang diskusi ini khusus Hardcore Cultist ke atas!");
      return;
    }
    if (!replyText.trim() || replyText.trim().length < 2 || !activePoll) {
      toast.info("Tulis pesan balasan terlebih dahulu.");
      return;
    }

    const msg = replyText.trim();
    setReplyingTo(null);
    setReplyText("");
    setReplyTargetUser("");

    try {
      const { data } = await api.post(
        `/community/polls/${activePoll.id}/comments/${commentId}/reply`,
        { message: msg }
      );
      if (data?.reply) {
        const updatedPolls = polls.map((p) => {
          if (p.id === activePoll.id) {
            const updatedCmts = (p.comments || []).map((c) =>
              c.id === commentId
                ? { ...c, replies: [...(c.replies || []), data.reply] }
                : c
            );
            return { ...p, comments: updatedCmts };
          }
          return p;
        });
        savePolls(updatedPolls);
      }
      toast.success("Balasan terkirim! 💬");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) || "Gagal mengirim balasan."
      );
    }
  };

  const handleToggleReaction = async (targetId, reactionType) => {
    if (!user) {
      toast.info("Silakan login untuk memberikan reaksi.");
      return;
    }
    if (!canVoteAndComment || !activePoll) {
      toast.info("✨ Reaksi komentar khusus member Hardcore Cultist ke atas!");
      return;
    }

    setActiveReactionPicker(null);

    try {
      const { data } = await api.post(
        `/community/polls/${activePoll.id}/comments/${targetId}/react`,
        { reaction_type: reactionType }
      );
      if (data?.entry) {
        const entry = data.entry;
        const updatedPolls = polls.map((p) => {
          if (p.id !== activePoll.id) return p;
          const updatedCmts = (p.comments || []).map((cm) => {
            if (cm.id === targetId) {
              return {
                ...cm,
                reactions: entry.reactions,
                user_reaction: entry.user_reaction,
              };
            }
            if (cm.replies && cm.replies.length > 0) {
              const updatedReps = cm.replies.map((rep) =>
                rep.id === targetId
                  ? {
                      ...rep,
                      reactions: entry.reactions,
                      user_reaction: entry.user_reaction,
                    }
                  : rep
              );
              return { ...cm, replies: updatedReps };
            }
            return cm;
          });
          return { ...p, comments: updatedCmts };
        });
        savePolls(updatedPolls);
      }
    } catch (err) {
      toast.error("Gagal memberikan reaksi.");
    }
  };

  const commentsList = activePoll?.comments || [];
  const totalComments = commentsList.reduce(
    (acc, c) => acc + 1 + (c.replies ? c.replies.length : 0),
    0
  );

  return (
    <section className="relative overflow-hidden rounded-3xl bg-zinc-950/80 border border-rose-500/30 p-5 sm:p-8 shadow-2xl shadow-rose-950/30 backdrop-blur-xl mb-14">
      {/* Decorative Glow */}
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-rose-600/10 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-purple-600/10 blur-[100px] pointer-events-none" />

      {/* TOP: Multi-Poll Switcher Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-zinc-800/80">
        <div className="flex flex-wrap items-center gap-2">
          {polls.map((p) => {
            const isActive = p.id === activePoll?.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePollId(p.id)}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 border ${
                  isActive
                    ? "bg-gradient-to-r from-rose-600 to-rose-700 text-white border-rose-500 shadow-md shadow-rose-950/60 ring-1 ring-rose-400"
                    : "bg-zinc-900/80 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 border-zinc-800"
                }`}
              >
                <span>{p.category || "Voting"}</span>
                {p.expires_at && new Date(p.expires_at).getTime() <= Date.now() ? (
                  <span className="text-[10px] px-1 rounded bg-black/40 text-zinc-400">Tutup 🔒</span>
                ) : null}
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
              </button>
            );
          })}
        </div>

        {/* Founder Controls: Sync & Add Poll */}
        {isFounder && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleForceSyncToServer}
              disabled={syncingServer}
              title="Upload & sinkronkan setelan voting di perangkat ini ke database server"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncingServer ? "animate-spin" : ""}`} />
              <span>{syncingServer ? "Menyinkronkan..." : "Sync ke Server ☁️"}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 text-xs font-bold transition-all active:scale-95"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Topik Voting 👑</span>
            </button>
          </div>
        )}
      </div>

      {(!activePoll || polls.length === 0) ? (
        <div className="py-16 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/30 mx-auto grid place-items-center text-rose-400">
            <Vote className="w-8 h-8" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-white">Belum Ada Topik Voting Aktif</h3>
            <p className="text-xs text-zinc-400 max-w-md mx-auto">
              Semua voting bawaan telah dibersihkan. Founder dapat membuat topik voting baru kapan saja.
            </p>
          </div>
          {isFounder && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white font-bold text-xs shadow-lg shadow-rose-950/50 transition active:scale-95 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>+ Buat Voting Baru Perdana 👑</span>
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Header Info */}
      <div className="relative mt-6 flex flex-col md:flex-row items-start md:items-end justify-between gap-4 pb-6 border-b border-zinc-800/80">
        <div className="space-y-1.5 max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-rose-600/20 to-rose-900/30 border border-rose-500/40 text-rose-300 text-xs font-semibold shadow-inner">
              <Vote className="w-3.5 h-3.5 text-rose-400" />
              <span>{activePoll.category}</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-medium">
              <Crown className="w-3 h-3" />
              <span>Hak Suara Hardcore Cultist</span>
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white tracking-tight pt-1">
            {activePoll.title}
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
            {activePoll.description}
          </p>
        </div>

        {/* Stats counter */}
        <div className="flex items-center gap-3 shrink-0 text-xs">
          <div className="flex items-center gap-1.5 text-zinc-400 bg-zinc-900/80 px-3 py-1.5 rounded-xl border border-zinc-800">
            <Users className="w-4 h-4 text-rose-400" />
            <span>
              <strong className="text-zinc-200 font-bold">{activePoll.total_votes || 0}</strong> Suara
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-zinc-400 bg-zinc-900/80 px-3 py-1.5 rounded-xl border border-zinc-800">
            <Clock className="w-4 h-4 text-amber-400" />
            <span>{formatRemainingTime(activePoll.expires_at, activePoll.ends_in)}</span>
          </div>
          {isFounder && (
            <button
              type="button"
              onClick={() => handleDeletePoll(activePoll.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 text-xs font-bold transition-all active:scale-95"
              title="Hapus topik voting ini"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Hapus Voting (Founder)</span>
            </button>
          )}
        </div>
      </div>

      {/* ================= SNEAK PEEK / PROOF PHOTO SECTION ================= */}
      {/* "bisa upload foto juga gw buat bukti yg komen sama yg liat tergantung votingan... dibawah itu kekunci tapi ga di blur biar orang penasaran" */}
      <div className="mt-6 p-4 sm:p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800/90 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-rose-600/20 border border-rose-500/40 grid place-items-center text-rose-400">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-white flex items-center gap-2">
                <span>{activePoll.proof_title || "Bukti Pengerjaan & Preview Langsung"}</span>
                {canAccessFullProof ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold">
                    <Unlock className="w-3 h-3" /> Akses Terbuka
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-bold">
                    <Lock className="w-3 h-3" /> Sneak Peek Hardcore
                  </span>
                )}
              </h4>
              <p className="text-[11px] text-zinc-400">{activePoll.proof_desc}</p>
            </div>
          </div>

          {/* Founder Upload Button */}
          {isFounder && (
            <div className="shrink-0">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleProofImageUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold border border-zinc-700 transition-all active:scale-95"
              >
                <Upload className="w-3.5 h-3.5 text-rose-400" />
                <span>Ganti Foto Bukti (Founder)</span>
              </button>
            </div>
          )}
        </div>

        {/* The Image Display with Teaser Lock (NO BLUR, BUT LOCKED WATERMARK + TEASER OVERLAY) */}
        <div className="relative overflow-hidden rounded-xl border border-zinc-800 bg-black/60 group">
          <img
            src={activeProofImage}
            alt="Bukti Sneak Peek"
            className={`w-full max-h-96 object-cover object-center transition-all duration-300 ${
              canAccessFullProof
                ? "cursor-pointer hover:scale-[1.01]"
                : "brightness-75 contrast-95 select-none pointer-events-none"
            }`}
            onClick={() => {
              if (canAccessFullProof) setViewingImage(activeProofImage);
            }}
          />

          {/* Top-Right Badge */}
          {canAccessFullProof ? (
            <button
              type="button"
              onClick={() => setViewingImage(activeProofImage)}
              className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-black/70 hover:bg-black/90 border border-white/20 text-white text-xs font-semibold backdrop-blur-md flex items-center gap-1.5 transition-all"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              <span>Perbesar 🔍</span>
            </button>
          ) : (
            /* Locked Teaser Overlay — Not Blurred, but Watermarked & Badged to spark curiosity */
            <>
              {/* Diagonal Watermark Banner */}
              <div className="absolute top-4 left-4 px-3 py-1 rounded-lg bg-amber-500/25 border border-amber-500/50 text-amber-200 text-xs font-extrabold backdrop-blur-md flex items-center gap-1.5 shadow-xl">
                <Lock className="w-3.5 h-3.5 text-amber-300" />
                <span>BUKTI SNEAK PEEK • HARDCORE CULTIST ONLY</span>
              </div>

              {/* Bottom Interactive Lock Banner */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/85 to-transparent flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                <div className="space-y-0.5">
                  <div className="text-xs sm:text-sm font-bold text-amber-300 flex items-center justify-center sm:justify-start gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span>Mau liat resolusi penuh & ikut nentuin hasil voting?</span>
                  </div>
                  <p className="text-[11px] text-zinc-300">
                    Gambar bukti dan hak suara terbuka khusus member <strong>Hardcore Cultist & God Tier</strong>.
                  </p>
                </div>

                <Link
                  to="/checkout/hardcore_cultist"
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs shadow-lg shadow-amber-950/60 flex items-center gap-1.5 transition-all shrink-0 active:scale-95"
                >
                  <Crown className="w-3.5 h-3.5" />
                  <span>Buka Akses (Rp 25.000)</span>
                  <ArrowRight className="w-3 h-3 ml-0.5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ================= OPTIONS VOTING GRID ================= */}
      <div className="mt-6 space-y-3">
        {activePoll.options.map((opt) => {
          const isSelected = selectedOptionId === opt.id;
          const isUserVotedThis = userCurrentVote === opt.id;
          const pct = opt.percentage || 0;
          const isWinner = isExpired && highestVote > 0 && opt.votes === highestVote;

          return (
            <div
              key={opt.id}
              onClick={() => {
                if (canVoteAndComment && !isExpired) setSelectedOptionId(opt.id);
              }}
              className={`relative overflow-hidden rounded-2xl border p-4 sm:p-5 transition-all ${
                canVoteAndComment && !isExpired ? "cursor-pointer" : "cursor-default"
              } ${
                isWinner
                  ? "bg-amber-950/25 border-amber-400/80 ring-1 ring-amber-400/60 shadow-xl shadow-amber-950/30"
                  : isSelected
                  ? "bg-rose-950/30 border-rose-500 shadow-lg shadow-rose-950/40 ring-1 ring-rose-500"
                  : "bg-zinc-900/50 hover:bg-zinc-900/80 border-zinc-800 hover:border-zinc-700"
              }`}
            >
              {/* Animated Progress Bar fill */}
              <div
                className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-rose-600/15 via-rose-500/10 to-transparent transition-all duration-700 pointer-events-none"
                style={{ width: `${pct}%` }}
              />

              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {canVoteAndComment && (
                    <div
                      className={`w-5 h-5 rounded-full border mt-0.5 grid place-items-center shrink-0 transition-colors ${
                        isSelected
                          ? "bg-rose-600 border-rose-500 text-white"
                          : "border-zinc-600 bg-zinc-800/80"
                      }`}
                    >
                      {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  )}

                  <div className="space-y-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm sm:text-base font-bold text-zinc-100 group-hover:text-white truncate">
                        {opt.title}
                      </h4>
                      {isWinner && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-amber-500 text-amber-950 text-[10px] font-black uppercase tracking-wider shadow">
                          <Trophy className="w-3 h-3" /> Pemenang Terpilih 🏆
                        </span>
                      )}
                      {opt.badge && !isWinner && (
                        <span className="px-2 py-0.5 rounded-md bg-zinc-800/90 border border-zinc-700 text-[10px] font-semibold text-rose-300">
                          {opt.badge}
                        </span>
                      )}
                      {isUserVotedThis && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-[10px] font-bold text-emerald-300">
                          <CheckCircle2 className="w-3 h-3" /> Pilihanmu
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 truncate">{opt.tag}</p>
                  </div>
                </div>

                {/* Percentage and Vote Count */}
                <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                  <div className="text-right">
                    <span className="text-base sm:text-lg font-black text-white font-mono">
                      {pct}%
                    </span>
                    <span className="text-[11px] text-zinc-500 block">
                      {opt.votes} Suara
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress bar line */}
              <div className="mt-3.5 w-full bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-rose-600 to-amber-500 h-1.5 rounded-full transition-all duration-1000"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Footer Bar */}
      <div className="mt-6 pt-5 border-t border-zinc-800/80">
        {isExpired ? (
          <div className="p-4 rounded-2xl bg-amber-950/20 border border-amber-500/40 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 text-xs text-amber-200">
              <Trophy className="w-5 h-5 text-amber-400 shrink-0" />
              <span>
                <strong>Voting telah selesai dan resmi ditutup!</strong> Opsi dengan suara tertinggi di atas telah terpilih sebagai pemenang komunitas.
              </span>
            </div>
            <span className="px-4 py-1.5 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-400 text-xs font-bold shrink-0">
              Voting Ditutup 🔒
            </span>
          </div>
        ) : canVoteAndComment ? (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-zinc-400 text-center sm:text-left">
              {userCurrentVote ? (
                <span className="flex items-center gap-1.5 text-emerald-300 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Suaramu sudah aktif tersimpan. Kamu masih bisa mengubah pilihan selama polling terbuka.
                </span>
              ) : (
                <span>
                  Pilih opsi di atas, lalu tekan tombol <strong>Kirim Suara Voting</strong>.
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleVote}
              disabled={submittingVote || !selectedOptionId}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 via-rose-700 to-rose-800 hover:from-rose-500 hover:to-rose-700 active:scale-95 text-white font-bold text-xs sm:text-sm shadow-xl shadow-rose-950/60 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {submittingVote ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Mencatat Suara...</span>
                </>
              ) : (
                <>
                  <Vote className="w-4 h-4" />
                  <span>{userCurrentVote ? "Perbarui Suara Voting" : "Kirim Suara Voting 🗳️"}</span>
                </>
              )}
            </button>
          </div>
        ) : (
          /* Conversion prompt for Free & Elite */
          <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-950/20 via-zinc-900 to-amber-950/20 border border-amber-500/30 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 grid place-items-center text-amber-400 shrink-0 mt-0.5">
                <Lock className="w-4 h-4" />
              </div>
              <div className="space-y-0.5">
                <h5 className="text-xs sm:text-sm font-bold text-amber-300">
                  Hak Suara Eksklusif Hardcore Cultist & God Tier
                </h5>
                <p className="text-xs text-zinc-400 leading-relaxed max-w-xl">
                  Ikut tentukan game dan format spesifikasi porting berikutnya yang akan digarap Founder.
                </p>
              </div>
            </div>

            <Link
              to="/checkout/hardcore_cultist"
              className="w-full md:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs sm:text-sm shadow-lg shadow-amber-950/50 flex items-center justify-center gap-1.5 transition-all shrink-0 active:scale-95"
            >
              <Crown className="w-4 h-4" />
              <span>Upgrade ke Hardcore (Rp 25.000)</span>
              <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
            </Link>
          </div>
        )}
      </div>

      {/* ================= VIP DISCUSSION & DEBATE LOUNGE ================= */}
      <div className="mt-8 pt-6 border-t border-zinc-800/80">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-rose-600/20 border border-rose-500/40 grid place-items-center text-rose-400">
              <MessageSquare className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
                <span>Ruang Diskusi & Debat VIP</span>
                <span className="px-2 py-0.5 rounded-full bg-rose-950/80 border border-rose-500/40 text-rose-300 text-[10px] font-semibold">
                  {totalComments} Pesan
                </span>
              </h3>
              <p className="text-[11px] text-zinc-400">
                Debat alasan pilihanmu untuk topik ini (Khusus Hardcore Cultist & Founder).
              </p>
            </div>
          </div>
        </div>

        {/* Comment Input Form — Always visible so the discussion feels alive, only the input is locked for lower tiers */}
        <div className="mb-6 space-y-2.5 p-3 sm:p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-1">
              <Avatar user={user} size={32} />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              {canVoteAndComment ? (
                <form onSubmit={handlePostComment} className="space-y-2">
                  <textarea
                    rows={2}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Ketik pendapatmu... Contoh: 'Gw vote yang 720p 2GB biar grafis bening!'"
                    className="w-full text-xs sm:text-sm bg-zinc-950/80 border border-zinc-800 rounded-xl p-3 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-rose-500 resize-none"
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-zinc-500">
                      {commentText.length}/500 karakter
                    </span>
                    <button
                      type="submit"
                      disabled={!commentText.trim()}
                      className="px-4 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 active:scale-95 text-white font-bold text-xs flex items-center gap-1.5 transition-all disabled:opacity-50"
                    >
                      <Send className="w-3 h-3" />
                      <span>Kirim Komentar</span>
                    </button>
                  </div>
                </form>
              ) : (
                /* Visible locked input box: people can see the discussion below, but cannot type */
                <div className="space-y-2">
                  <div className="relative">
                    <textarea
                      rows={2}
                      disabled
                      placeholder="🔒 Kolom komentar ini khusus member Hardcore Cultist ke atas. Kamu tetap bisa membaca seluruh jalannya debat di bawah!"
                      className="w-full text-xs sm:text-sm bg-zinc-950/50 border border-zinc-800/80 rounded-xl p-3 text-zinc-400 placeholder:text-zinc-500 resize-none cursor-not-allowed select-none"
                    />
                    <div className="absolute top-2.5 right-2.5 p-1 rounded-md bg-zinc-900/80 border border-zinc-700/80 text-amber-400">
                      <Lock className="w-3.5 h-3.5" />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-0.5">
                    <span className="text-[11px] text-zinc-400">
                      Mau ikut berpendapat & menentukan hasil akhir?
                    </span>
                    <Link
                      to="/checkout/hardcore_cultist"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs shadow-md shadow-amber-950/40 transition-all active:scale-95"
                    >
                      <Crown className="w-3 h-3" />
                      <span>Buka Akses Komen (Rp 25.000)</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-4">
          {commentsList.map((cm) => {
            const myReactionObj = REACTIONS.find((r) => r.type === cm.user_reaction);

            return (
              <div
                key={cm.id}
                className="p-3 sm:p-4 rounded-2xl bg-zinc-900/40 border border-zinc-800/70 hover:border-zinc-700/80 transition-all space-y-2"
              >
                {/* Comment Header */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar
                      user={{
                        name: cm.user_name,
                        avatar_url: cm.user_avatar || cm.avatar_url,
                        role: cm.user_role,
                        badges: cm.badges,
                      }}
                      size={28}
                      className="shrink-0"
                    />
                    <Username
                      name={cm.user_name}
                      role={cm.user_role}
                      className="text-xs sm:text-sm font-bold text-zinc-100 truncate"
                    />
                    <RoleBadge role={cm.user_role} badges={cm.badges} size="xs" />
                    {cm.voted_for_title && (
                      <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300">
                        Voted: {cm.voted_for_title} 🎮
                      </span>
                    )}
                  </div>

                  <span className="text-[10px] text-zinc-500 shrink-0">
                    {formatCommentTime(cm.created_at)}
                  </span>
                </div>

                {cm.voted_for_title && (
                  <div className="sm:hidden">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-semibold text-emerald-300">
                      Voted: {cm.voted_for_title} 🎮
                    </span>
                  </div>
                )}

                {/* Message Body */}
                <p className="text-xs sm:text-sm text-zinc-200 leading-relaxed pl-9">
                  {cm.message}
                </p>

                {/* Reaction Counter Pills */}
                <div className="pl-9 flex flex-wrap items-center gap-1.5 pt-0.5">
                  {REACTIONS.map((r) => {
                    const count = cm.reactions?.[r.type] || 0;
                    if (count <= 0) return null;
                    const isMyReaction = cm.user_reaction === r.type;
                    return (
                      <button
                        key={r.type}
                        type="button"
                        onClick={() => handleToggleReaction(cm.id, r.type)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all active:scale-95 ${
                          isMyReaction
                            ? "bg-rose-500/20 border-rose-500/60 text-rose-300 shadow-sm shadow-rose-950"
                            : "bg-zinc-950/80 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        <span>{r.emoji}</span>
                        <span className="font-bold">{count}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Action Bar (Reaksi & Balas) */}
                <div className="pl-9 pt-1.5 flex items-center gap-3 text-xs font-semibold relative">
                  {/* Reaction Button & Dock */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveReactionPicker(
                          activeReactionPicker === cm.id ? null : cm.id
                        )
                      }
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all active:scale-95 ${
                        cm.user_reaction
                          ? "text-rose-400 bg-rose-500/10"
                          : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
                      }`}
                    >
                      <span className="text-sm">
                        {myReactionObj ? myReactionObj.emoji : "👍"}
                      </span>
                      <span>{myReactionObj ? myReactionObj.label : "Suka"}</span>
                    </button>

                    {activeReactionPicker === cm.id && (
                      <div className="absolute -top-11 left-0 z-30 flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-zinc-950 border border-zinc-700/80 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-90 duration-150">
                        {REACTIONS.map((r) => (
                          <button
                            key={r.type}
                            type="button"
                            onClick={() => handleToggleReaction(cm.id, r.type)}
                            className={`text-lg sm:text-xl p-1 rounded-full transition-transform hover:scale-135 active:scale-90 ${r.bgHover}`}
                            title={r.label}
                          >
                            {r.emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reply Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!canVoteAndComment) {
                        toast.error("Balasan diskusi eksklusif untuk member Hardcore Cultist ke atas!");
                        return;
                      }
                      setReplyingTo(cm.id);
                      setReplyTargetUser(cm.user_name);
                      setReplyText(`@${cm.user_name} `);
                    }}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 transition-all active:scale-95"
                  >
                    <Reply className="w-3.5 h-3.5" />
                    <span>Balas</span>
                  </button>

                  {cm.replies?.length > 0 && (
                    <span className="text-[11px] text-zinc-500 font-normal">
                      • {cm.replies.length} balasan
                    </span>
                  )}
                </div>

                {/* Nested Threaded Replies */}
                {cm.replies && cm.replies.length > 0 && (
                  <div className="mt-3 ml-4 sm:ml-9 pl-3.5 border-l-2 border-rose-500/25 space-y-2.5">
                    {cm.replies.map((rep) => {
                      const repMyReaction = REACTIONS.find((r) => r.type === rep.user_reaction);

                      return (
                        <div
                          key={rep.id}
                          className="p-3 rounded-xl bg-zinc-950/60 border border-zinc-800/80 hover:border-zinc-700/80 transition-all space-y-1.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Avatar
                                user={{
                                  name: rep.user_name,
                                  avatar_url: rep.user_avatar || rep.avatar_url,
                                  role: rep.user_role,
                                  badges: rep.badges,
                                }}
                                size={24}
                                className="shrink-0"
                              />
                              <Username
                                name={rep.user_name}
                                role={rep.user_role}
                                className="text-xs font-bold text-zinc-200 truncate"
                              />
                              <RoleBadge role={rep.user_role} badges={rep.badges} size="xs" />
                              {rep.voted_for_title && (
                                <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-500/10 border border-emerald-500/25 text-[9px] font-semibold text-emerald-300">
                                  Voted: {rep.voted_for_title} 🎮
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-zinc-500 shrink-0">
                              {formatCommentTime(rep.created_at)}
                            </span>
                          </div>

                          <p className="text-xs text-zinc-300 leading-relaxed pl-8">
                            {rep.message}
                          </p>

                          {/* Reply Reaction Pills */}
                          <div className="pl-8 flex flex-wrap items-center gap-1 pt-0.5">
                            {REACTIONS.map((r) => {
                              const count = rep.reactions?.[r.type] || 0;
                              if (count <= 0) return null;
                              const isMyReaction = rep.user_reaction === r.type;
                              return (
                                <button
                                  key={r.type}
                                  type="button"
                                  onClick={() => handleToggleReaction(rep.id, r.type)}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border transition-all active:scale-95 ${
                                    isMyReaction
                                      ? "bg-rose-500/20 border-rose-500/60 text-rose-300"
                                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                                  }`}
                                >
                                  <span>{r.emoji}</span>
                                  <span className="font-bold">{count}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Reply Action Bar */}
                          <div className="pl-8 pt-1 flex items-center gap-3 text-xs font-semibold relative">
                            <button
                              type="button"
                              onClick={() =>
                                setActiveReactionPicker(
                                  activeReactionPicker === rep.id ? null : rep.id
                                )
                              }
                              className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded transition-all active:scale-95 ${
                                rep.user_reaction ? "text-rose-400 font-bold" : "text-zinc-500 hover:text-zinc-300"
                              }`}
                            >
                              <span>{repMyReaction ? repMyReaction.emoji : "👍"}</span>
                              <span>{repMyReaction ? repMyReaction.label : "Suka"}</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (!canVoteAndComment) {
                                  toast.error("Ruang diskusi ini khusus Hardcore Cultist ke atas!");
                                  return;
                                }
                                setReplyingTo(cm.id);
                                setReplyTargetUser(rep.user_name);
                                setReplyText(`@${rep.user_name} `);
                              }}
                              className="text-[11px] text-zinc-500 hover:text-rose-400 transition-colors"
                            >
                              Balas
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Inline Reply Input Box */}
                {replyingTo === cm.id && (
                  <div className="mt-3 ml-4 sm:ml-9 pl-3.5 border-l-2 border-rose-500/40">
                    <div className="p-3 rounded-2xl bg-zinc-950/90 border border-rose-500/30 space-y-2.5 shadow-xl">
                      <div className="flex items-center justify-between text-xs text-rose-300 font-semibold">
                        <span className="flex items-center gap-1.5">
                          <Reply className="w-3.5 h-3.5 text-rose-400" />
                          Membalas <strong>@{replyTargetUser || cm.user_name}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText("");
                          }}
                          className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <textarea
                        rows={2}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`Ketik balasanmu untuk @${replyTargetUser || cm.user_name}...`}
                        className="w-full text-xs bg-zinc-900 border border-zinc-800 rounded-xl p-2.5 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-rose-500 resize-none"
                        autoFocus
                        maxLength={400}
                      />

                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-500">
                          {replyText.length}/400 karakter
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyText("");
                            }}
                            className="px-3 py-1 rounded-lg text-xs text-zinc-400 hover:text-white"
                          >
                            Batal
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSubmitReply(cm.id)}
                            disabled={!replyText.trim()}
                            className="px-4 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 active:scale-95 text-white font-bold text-xs flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <Send className="w-3 h-3" />
                            <span>Kirim Balasan</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  )}

      {/* ================= FULL IMAGE ZOOM MODAL ================= */}
      {viewingImage && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setViewingImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/20 bg-zinc-950 p-2 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setViewingImage(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/80 hover:bg-black text-white border border-white/20 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={viewingImage}
              alt="Bukti Sneak Peek Full"
              className="w-full h-auto max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>,
        document.body
      )}

      {/* ================= FOUNDER CREATE POLL MODAL ================= */}
      {showCreateModal && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-150">
          <div className="relative w-full max-w-2xl h-[88vh] max-h-[640px] flex flex-col rounded-2xl sm:rounded-3xl bg-zinc-950 border border-amber-500/40 shadow-2xl shadow-amber-950/50 overflow-hidden">
            {/* Sticky Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-zinc-800 bg-zinc-900/95 shrink-0 z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/40 grid place-items-center text-amber-400 shrink-0">
                  <Crown className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-white">
                    Buat Topik Voting Baru (Founder Panel)
                  </h3>
                  <p className="text-[11px] text-zinc-400">
                    Bisa voting engine game, genre cerita, format porting, dll.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form wrapping body + sticky footer */}
            <form onSubmit={handleCreatePoll} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Scrollable Body */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-4 text-xs">
                {/* Quick 1-Click Templates */}
                <div className="p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 space-y-2">
                  <span className="text-[11px] font-bold text-amber-300 block">
                    ⚡ Template Cepat 1-Klik:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setNewTitle("Next Update Mau Game Engine Apa? 🕹️");
                        setNewCategory("Pilihan Engine ⚙️");
                        setNewDesc("Tentukan engine game favorit kalian untuk proyek terjemahan berikutnya yang akan digarap Founder Kyouka Reimei! Hak suara eksklusif Hardcore Cultist & God Tier.");
                        setNewOptions([
                          { title: "Unity Engine", tag: "Grafis Modern 2D/3D & Visual Bening" },
                          { title: "RPG Maker (RPGM)", tag: "Klasik RPG & Gameplay Cerita Padat" },
                          { title: "Ren'Py", tag: "Visual Novel Interaktif & Full Voice" },
                        ]);
                        toast.info("Template Engine Game terpasang! 🕹️");
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-[11px] font-medium hover:text-amber-300 transition"
                    >
                      🕹️ Engine (Unity / RPGM / Ren'Py)
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setNewTitle("Kalian Lebih Suka Genre Game Apa Buat Next Project? 🎭");
                        setNewCategory("Pilihan Genre 🏷️");
                        setNewDesc("Vote preferensi genre cerita favorit kalian untuk game rilisan bulan ini. Hak suara eksklusif untuk Hardcore Cultist & God Tier!");
                        setNewOptions([
                          { title: "NTR (Netorare)", tag: "Drama Intens & Konflik Emosional Panas" },
                          { title: "Pure Love / No NTR", tag: "Romantis Wholesome & Cerita Manis" },
                          { title: "Bebas / Campuran", tag: "Ikuti Selera & Kurasi Founder Kyouka Reimei" },
                        ]);
                        toast.info("Template Genre Cerita terpasang! 🎭");
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-[11px] font-medium hover:text-rose-300 transition"
                    >
                      🎭 Genre (NTR / No NTR / Bebas)
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setNewTitle("Voting Format Porting Android: Monster Black Market DLC 2 📱");
                        setNewCategory("Porting Android 📱");
                        setNewDesc("Pilih format APK yang paling kalian butuhkan untuk smartphone kalian.");
                        setNewOptions([
                          { title: "HD 720p Kualitas Maksimal (~2.1 GB)", tag: "Grafis Super Bening 60 FPS Lossless" },
                          { title: "Lebih Burix / Hemat Memori (~950 MB)", tag: "Ramah HP Kentang & Hemat Kuota" },
                        ]);
                        toast.info("Template Porting Android terpasang! 📱");
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-[11px] font-medium hover:text-emerald-300 transition"
                    >
                      📱 Porting Android (720p / Burix)
                    </button>
                  </div>
                </div>

                {/* Poll Title */}
                <div className="space-y-1">
                  <label className="text-zinc-300 font-bold">Judul Voting:</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Contoh: Next Update Mau Game Engine Apa?"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-amber-500"
                    required
                  />
                </div>

                {/* Category & Gated Role */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-zinc-300 font-bold">Kategori / Label Tab:</label>
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Contoh: Pilihan Engine ⚙️"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-amber-500"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-zinc-300 font-bold">Hak Akses Suara & Bukti:</label>
                    <select
                      value={newMinRole}
                      onChange={(e) => setNewMinRole(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-amber-500"
                    >
                      <option value="hardcore_cultist">Eksklusif Hardcore Cultist & God Tier</option>
                      <option value="all">Bebas Semua Member (Termasuk Free)</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <label className="text-zinc-300 font-bold">Deskripsi Penjelasan:</label>
                  <textarea
                    rows={2}
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    placeholder="Jelaskan apa yang divotingkan..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-2.5 text-white focus:outline-none focus:border-amber-500 resize-none"
                  />
                </div>

                {/* Proof Photo Upload */}
                <div className="space-y-2 p-3 rounded-2xl bg-zinc-900/60 border border-zinc-800">
                  <label className="text-zinc-300 font-bold flex items-center gap-1.5">
                    <Camera className="w-4 h-4 text-amber-400" />
                    <span>Foto Bukti Translasi / Sneak Peek (Opsional):</span>
                  </label>
                  <input
                    type="text"
                    value={newProofDesc}
                    onChange={(e) => setNewProofDesc(e.target.value)}
                    placeholder="Keterangan foto... (Opsional)"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2 text-zinc-200"
                  />
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const dataUrl = await compressImageDataUrl(file);
                        setNewProofImage(dataUrl);
                      }}
                      className="text-xs text-zinc-400 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-zinc-800 file:text-zinc-200 hover:file:bg-zinc-700"
                    />
                    {newProofImage && (
                      <span className="text-[11px] text-emerald-400 font-medium">
                        ✓ Foto terlampir
                      </span>
                    )}
                  </div>
                </div>

                {/* Expiration Scheduling */}
                <div className="space-y-1.5 p-3 rounded-2xl bg-zinc-900/60 border border-zinc-800">
                  <label className="text-zinc-300 font-bold flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>Jadwal Batas Waktu / Kadaluarsa Voting:</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div>
                      <span className="text-[11px] text-zinc-400 block mb-1">Durasi Cepat:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { label: "3 Hari", days: 3 },
                          { label: "5 Hari", days: 5 },
                          { label: "7 Hari", days: 7 },
                          { label: "14 Hari", days: 14 },
                          { label: "30 Hari", days: 30 },
                        ].map((d) => (
                          <button
                            key={d.label}
                            type="button"
                            onClick={() => {
                              const dt = new Date();
                              dt.setDate(dt.getDate() + d.days);
                              const pad = (n) => String(n).padStart(2, "0");
                              setNewExpiresCustom(
                                `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
                              );
                            }}
                            className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] border border-zinc-700 transition"
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-zinc-400 block mb-1">Atau Tentukan Tanggal & Jam Berakhir:</span>
                      <input
                        type="datetime-local"
                        value={newExpiresCustom}
                        onChange={(e) => setNewExpiresCustom(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2 text-white text-xs focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Dynamic Candidate Options */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-zinc-300 font-bold">Pilihan Opsi Voting (Min. 2):</label>
                    <button
                      type="button"
                      onClick={handleAddPollOption}
                      className="text-xs text-amber-400 hover:underline flex items-center gap-1 font-semibold"
                    >
                      <Plus className="w-3 h-3" /> Tambah Opsi
                    </button>
                  </div>

                  <div className="space-y-2">
                    {newOptions.map((opt, idx) => (
                      <div
                        key={idx}
                        className="p-2.5 rounded-xl bg-zinc-900/80 border border-zinc-800 flex items-center gap-2"
                      >
                        <span className="w-5 h-5 rounded-full bg-zinc-800 grid place-items-center text-[10px] font-bold text-zinc-400 shrink-0">
                          {idx + 1}
                        </span>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={opt.title}
                            onChange={(e) => {
                              const copy = [...newOptions];
                              copy[idx].title = e.target.value;
                              setNewOptions(copy);
                            }}
                            placeholder={`Judul Opsi ${idx + 1} (misal: Unity)`}
                            className="bg-zinc-950 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-white focus:outline-none focus:border-amber-500"
                            required
                          />
                          <input
                            type="text"
                            value={opt.tag}
                            onChange={(e) => {
                              const copy = [...newOptions];
                              copy[idx].tag = e.target.value;
                              setNewOptions(copy);
                            }}
                            placeholder="Keterangan singkat (misal: Grafis Modern 3D/2D)"
                            className="bg-zinc-950 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-300 focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        {newOptions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => handleRemovePollOption(idx)}
                            className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-zinc-800 transition"
                            title="Hapus opsi ini"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sticky Footer: ALWAYS VISIBLE AND PINNED TO BOTTOM */}
              <div className="flex items-center justify-between p-3.5 sm:p-4 border-t border-zinc-800 bg-zinc-900/95 shrink-0 z-10">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 text-xs font-semibold transition"
                >
                  Batal
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAddPollOption}
                    className="px-3.5 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold border border-zinc-700 inline-flex items-center gap-1 transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Opsi</span>
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs flex items-center gap-1.5 shadow-lg shadow-amber-950/50 transition-all active:scale-95"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Terbitkan Topik Voting 🚀</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ,
        document.body
      )}
    </section>
  );
}
